/**
 * Presets are values, not behavior.
 *
 * Every one of these lands in a shader uniform and nothing about them is baked
 * into the shader logic, so retuning is a uniform write - no rebuild, no
 * bitmap, no reload. That is deliberate: these numbers are NOT final, and the
 * whole point of the port was to make changing them cost nothing.
 *
 * The two that define the look are shadowKeep and highlightKeep: what fraction
 * of its light a black pixel and a white pixel come away with. Read them as
 * "the shadows keep 36% of what they had, sunlit stucco keeps 3.5%". The sun
 * leaving the picture is the gap between those two numbers; how dark the
 * result is overall is where they both sit.
 */

export type NightPresetId = 'dusk' | 'night' | 'darknight'

export interface NightParams {
  shadowKeep: number
  highlightKeep: number
  knee: number
  exposureComp: number
  chromaStrength: number
  chromaResponse: number
  coolShadows: number
  tint: number
  contrast: number
  blackFloor: number
  texturePreservation: number
  vignette: number
  skyExtra: number
  skyFacing: number
  /** Extra light given up by strongly colored surfaces. See the shader. */
  chromaDarkening: number
}

export const NIGHT_PRESETS: Record<NightPresetId, NightParams> = {
  /**
   * The one that sells the job, and the default.
   *
   * Not the darkest - the most useful. A homeowner has to see their own house
   * clearly enough to recognize the roof, the trim, the windows and the
   * planting, while the lights are unmistakably the brightest thing in the
   * frame. Expensive blue hour real estate photography, not surveillance
   * footage.
   */
  dusk: {
    shadowKeep: 0.361,
    highlightKeep: 0.1865,
    knee: 2.0,
    exposureComp: 0,
    chromaStrength: 0.28,
    chromaResponse: 0.78,
    coolShadows: 0.18,
    tint: 0.03,
    contrast: 0.1,
    blackFloor: 0.0012,
    texturePreservation: 0.32,
    vignette: 0.06,
    skyExtra: -1.767,
    skyFacing: 0.33,
    chromaDarkening: 0.35,
  },

  /** Deeper, but the architecture still has to be obvious at a glance. */
  night: {
    shadowKeep: 0.2407,
    highlightKeep: 0.1143,
    knee: 2.1,
    exposureComp: 0,
    chromaStrength: 0.38,
    chromaResponse: 0.78,
    coolShadows: 0.24,
    tint: 0.04,
    contrast: 0.13,
    blackFloor: 0.0018,
    texturePreservation: 0.34,
    vignette: 0.09,
    skyExtra: -2.167,
    skyFacing: 0.44,
    chromaDarkening: 0.42,
  },

  /**
   * Secondary, and currently too dark to ship as it stands - it puts more than
   * half the frame below the readable threshold, which is not what this tool
   * is for. Kept as the dramatic option and deliberately NOT the preset the
   * rest of the system is tuned around.
   */
  darknight: {
    shadowKeep: 0.1624,
    highlightKeep: 0.0722,
    knee: 2.2,
    exposureComp: 0,
    chromaStrength: 0.46,
    chromaResponse: 0.78,
    coolShadows: 0.3,
    tint: 0.05,
    contrast: 0.16,
    blackFloor: 0.0024,
    texturePreservation: 0.27,
    vignette: 0.11,
    skyExtra: -2.467,
    skyFacing: 0.7,
    chromaDarkening: 0.48,
  },
}

export const PRESET_ORDER: NightPresetId[] = ['dusk', 'night', 'darknight']

export const PRESET_LABEL: Record<NightPresetId, string> = {
  dusk: 'Dusk',
  night: 'Night',
  darknight: 'Dark night',
}
